/*
  The orders register's words, verbatim from the prototype (screens/orders.html,
  scripts/screens/orders.js, scripts/data.js) where it has them, and written in the same voice
  where it does not: the prototype has no sortable headers and no export that can fail.
*/
/** How the instalment builder names its rows and counts them, as the prototype's renderInstal() does. */
const ORDINALS = [
    'First',
    'Second',
    'Third',
    'Fourth',
    'Fifth',
    'Sixth',
    'Seventh',
    'Eighth',
    'Ninth',
    'Tenth',
];
const COUNT_WORDS = [
    '',
    'one',
    'two',
    'three',
    'four',
    'five',
    'six',
    'seven',
    'eight',
    'nine',
    'ten',
];

/** Past the ten the design writes out: 11th, 21st, 22nd, 23rd, 111th (QA on ALS-25, 7 Oct 2026). */
function numberedOrdinal(n: number): string {
    const lastTwo = n % 100;
    if (lastTwo >= 11 && lastTwo <= 13) return `${n}th`;
    return `${n}${['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;
}

export const COPY = {
    eyebrow: 'Orders',
    title: 'Orders',
    cardTitle: 'Orders',
    add: 'New order',
    export: 'Export',
    exportFailed: 'The export could not be prepared.',
    /** The card's badge: what the register matched, uppercase beside the title. */
    badge: (n: number) => `${n.toLocaleString('en-GB')} ${n === 1 ? 'ORDER' : 'ORDERS'}`,
    columns: {
        expand: 'Expand',
        products: 'Products',
        customer: 'Customer / order no',
        /** The client record's first column (ALS-169), where the customer goes without saying. */
        orderNo: 'Order no',
        date: 'Order date / due',
        net: 'Net amount',
        payment: 'Payment',
        status: 'Order status',
        actions: 'Actions',
    },
    /** The row's two buttons. Each one names the order it acts on, since a register has many. */
    row: {
        edit: 'Edit',
        editLabel: (orderNo: string) => `Edit order ${orderNo}`,
        view: 'View order',
        viewLabel: (orderNo: string) => `View order ${orderNo}`,
    },
    /**
     * The row's chevron and the "+N" tile both open the row. One fixed name each, since the
     * button's aria-expanded already says whether it is open.
     */
    expand: {
        label: (orderNo: string) => `Details for order ${orderNo}`,
        moreOpen: (n: number) => `Show all ${n} items`,
        moreClose: 'Collapse',
    },
    /**
     * The expanded row (ALS-58), verbatim from scripts/components/order-expansion.js. Where the
     * record carries nothing the words stay, greyed, rather than the fact vanishing.
     */
    expansion: {
        products: 'Products details',
        productCount: (n: number) =>
            `${n.toLocaleString('en-GB')} ${n === 1 ? 'product' : 'products'}`,
        order: 'Order details',
        loading: 'Loading the order',
        failed: 'This order could not be loaded.',
        retry: 'Try again',
        noLines: 'No products left on this order.',
        preOrder: 'Pre-order',
        dropShip: 'Drop ship',
        description: 'Description',
        notSet: 'Not set',
        notInvoiced: 'Not invoiced',
        notApplicable: 'Not applicable',
        // The prototype's word for an order with no documents yet (order-expansion.js).
        noDocuments: 'None yet',
        /** A waybill as the prototype names it in the Documents fact: "DHL 2534487384". */
        waybill: (waybill: string) => `DHL ${waybill}`,
        none: 'None',
        /**
         * Edit payment details (ALS-361), verbatim from screens/order-detail.html's od-pay-panel and
         * order-detail.js's renderPayForm(), in its voice where it has no words.
         */
        editPlan: {
            title: 'Edit payment details',
            subtitle: (orderNo: string, count: number, net: string) =>
                `${orderNo} · ${count} ${count === 1 ? 'instalment' : 'instalments'} · ${net}`,
            carried: (paid: string) => `${paid} already paid is carried over`,
            carriedBody: (outstanding: string, paidRows: number) =>
                `Changing the count or the split redivides the ${outstanding} still outstanding. The paid instalment${paidRows === 1 ? ' keeps' : 's keep'} its amount, date and type.`,
            plan: 'Plan',
            count: 'Number of instalments',
            fewer: 'Fewer instalments',
            more: 'More instalments',
            split: 'Split',
            cardFee: 'Card fee',
            cardFeeValue: (percentage: string) => `${percentage}% · order level`,
            schedule: 'Schedule',
            scheduleNote: 'paid rows stay as they are',
            columns: {
                number: '#',
                amount: 'Amount',
                dueDate: 'Due date',
                paymentType: 'Payment type',
                status: 'Status',
            },
            row: (n: number) => `instalment ${n}`,
            sentencePaid: (paidRows: number) =>
                `Instalment ${paidRows === 1 ? '1 is' : `1 to ${paidRows} are`} paid and shown for the record.`,
            sentenceEqual: (left: string, open: number) =>
                `The remaining ${left} is split evenly across ${open === 1 ? 'one instalment' : `${open} instalments`}.`,
            sentenceCustom: (left: string, open: number) =>
                `The remaining ${left} is set by hand across ${open === 1 ? 'one instalment' : `${open} instalments`}.`,
            totals: {
                paid: 'Paid',
                stillToPay: 'Still to pay',
                scheduled: 'Scheduled total',
                difference: 'Difference',
            },
            /** What the order's total becomes when a payment type moves the card fee: ours. */
            newTotal: (net: string) =>
                `The order total becomes ${net}, as the card fee follows the payment types.`,
            editable: (open: number, count: number) => `${open} of ${count} instalments editable`,
            footNote: 'Saving does not resend payment links',
            previewFailed: 'The new schedule could not be worked out. Try again in a moment.',
            cancel: 'Cancel',
            save: 'Save changes',
            done: (count: number) => `Payment details saved · ${count} instalments`,
            failed: 'The payment details could not be saved.',
        },
        yes: 'Yes',
        no: 'No',
        line: {
            supplier: 'Supplier',
            variant: 'Variant',
            qty: 'Qty',
            unitPrice: 'Unit price',
            commission: 'Commission',
            origin: 'Origin',
            status: 'Status',
            inWardrobe: 'In wardrobe',
            estDelivery: 'Est. delivery',
        },
        facts: {
            orderNo: 'Order number',
            invoiceType: 'Invoice type',
            invoiceNo: 'Invoice number',
            invoiceDate: 'Invoice date',
            reference: 'Reference',
            amountCard: 'Amount card',
            amountWallet: 'Amount wallet',
            shippingAddress: 'Shipping address',
            phone: 'Phone',
            shippingCountry: 'Shipping country',
            billingCountry: 'Billing country',
            tracking: 'Tracking number',
            estDelivery: 'Estimated delivery',
            deliveryComment: 'Delivery comment',
            customerCountry: 'Customer country',
            assignedTo: 'Assigned to',
            assignedOn: 'Assigned on',
            cancellationReason: 'Cancellation reason',
            documents: 'Documents',
        },
    },
    /** The mobile sort control labels the columns more briefly than the header does. */
    sort: {
        label: 'Sort orders',
        customer: 'Customer',
        date: 'Order date',
        net: 'Net amount',
        payment: 'Payment',
        status: 'Order status',
    },
    /** The footer's range, as the prototype writes it: "Showing 1-10 of 42 orders". */
    range: (from: number, to: number, total: number) =>
        total <= to - from + 1
            ? `Showing ${total.toLocaleString('en-GB')} ${total === 1 ? 'order' : 'orders'}`
            : `Showing ${from.toLocaleString('en-GB')}-${to.toLocaleString('en-GB')} of ${total.toLocaleString('en-GB')} orders`,
    items: (n: number) => `${n.toLocaleString('en-GB')} ${n === 1 ? 'item' : 'items'}`,
    /** The "+N" tile: what it stands for, for the tooltip and the screen reader. */
    moreItems: (n: number) => `${n} more ${n === 1 ? 'item' : 'items'}`,
    /** The base-currency line under a non-sterling amount. */
    base: (amount: string) => `≈ ${amount} base`,
    searchPlaceholder: 'Search orders, clients, products…',
    searchLabel: 'Search orders, clients and products',
    loading: 'Loading orders',
    /** The card's status line once the drawer has applied something, as the prototype writes it. */
    filtered: (n: number) => `Filtered · ${n} ${n === 1 ? 'filter' : 'filters'}`,
    noMatch: {
        title: 'No orders match',
        body: 'Nothing matches the search and filters together.',
        action: 'Clear filters',
    },
    empty: {
        title: 'No orders yet',
        body: 'Create the first order and it will appear here with its invoice.',
    },
    failed: {
        title: 'Orders could not be loaded',
        body: 'A problem reading the data, not a problem with Xero. No orders have been lost.',
        action: 'Try again',
    },
    /**
     * The order's own page (ALS-59, ALS-60, ALS-61), verbatim from screens/order-detail.html and
     * scripts/screens/order-detail.js where it has the words. Where this build's rules differ
     * from the prototype's (nothing here moves money, a paid order is not cancelled here, a
     * deleted order opens read only), the words are new and in the same voice.
     */
    record: {
        breadcrumb: 'Orders',
        title: 'Order',
        subtitle: (date: string, source: string | null, createdBy: string | null) =>
            [
                `Placed ${date}${source ? ` via ${source}` : ''}`,
                createdBy ? `Created by ${createdBy}` : null,
            ]
                .filter(Boolean)
                .join(' · '),
        deleted: 'Deleted',
        edit: 'Edit order',
        cancel: 'Cancel order',
        /** Why Cancel order is refused, for its tooltip. The order's reasons come before the person's. */
        cancelRefused: {
            Deleted: 'A deleted order cannot be cancelled.',
            AlreadyCancelled: 'A cancelled order cannot be cancelled.',
            Completed: 'A completed order cannot be cancelled.',
            NotPermitted: 'Your account cannot cancel an order. Ask an admin.',
        },
        editRefused: 'Your role cannot edit orders. Ask an admin.',
        /** Re-raise (ALS-260): the header's action on a cancelled order, and the band's links. */
        reraise: 'Re-raise order',
        reraisedAs: 'Raised again as',
        reraisedFrom: 'Raised again from',
        /** Why the Items tab's Add product will not open (ALS-259). */
        addRefused: {
            MoneyTaken:
                'Money has been taken on this order, so it takes no new products. Raise a new order for them.',
            Cancelled: 'A cancelled order takes no new products.',
            Deleted: 'A deleted order takes no new products.',
            Completed: 'A completed order takes no new products. Raise a new order for them.',
        },
        added: (n: number) =>
            `${n.toLocaleString('en-GB')} ${n === 1 ? 'product' : 'products'} added · totals recalculated`,
        addFailed: 'The products could not be added.',
        missing: {
            title: 'This order could not be found',
            body: 'It does not exist, or the link is out of date.',
            action: 'Back to orders',
        },
        failed: {
            title: 'This order could not be loaded',
            body: 'A problem reading the record. The order, its invoices and its shipments are untouched.',
            action: 'Try again',
        },
        /** The band at the top of the summary card. */
        band: {
            deleted: 'Order deleted.',
            deletedBody: 'Kept for the record. Nothing on it can be changed.',
            cancelled: 'Order cancelled.',
            refunded: 'Refunded in full.',
            paid: (amount: string) => `Paid in full · ${amount} received.`,
            /** "Balance £1,250 due by 12-Sep-2026 · instalment 2 of 3." */
            balance: (amount: string, by: string | null, instalment: string | null) =>
                `Balance ${amount} due${by ? ` by ${by}` : ''}${instalment ? ` · ${instalment}` : ''}.`,
            instalment: (n: number, of: number) => `instalment ${n} of ${of}`,
            deposit: (amount: string, on: string | null) =>
                `Deposit of ${amount} received${on ? ` on ${on}` : ''}.`,
        },
        client: {
            wallet: 'Wallet credit',
            noWallet: 'No wallet',
            rm: 'RM',
            removed: 'Removed from the client’s addresses since',
            shipping: 'Shipping address',
            billing: 'Billing address',
            phone: 'Phone',
            email: 'Email',
            relationshipManager: 'Relationship manager',
        },
        facts: {
            status: 'Order status',
            orderNo: 'Order number',
            orderDate: 'Order date',
            xeroReference: 'Xero reference',
            notSynced: 'Not synced',
            invoiceNo: 'Invoice number',
            noInvoice: 'No invoice on record',
            invoiceDate: 'Invoice date',
            invoiceType: 'Invoice type',
            currency: 'Currency',
            tracking: 'Tracking',
            saleSource: 'Sale source',
            salesTeam: 'Sales team',
            occasion: 'Occasion',
            notSet: 'Not set',
            followUp: 'Follow-up',
            nonePlanned: 'None planned',
            assignedTo: 'Assigned to',
            none: '\u2013',
        },
        money: {
            payment: 'Payment status',
            net: 'Net amount',
            margin: 'Estimated margin',
            base: (amount: string) => `≈ ${amount} in base currency`,
            commission: 'Commission',
            commissionDue: (amount: string) => `${amount} due from suppliers`,
            commissionPaid: (amount: string) => `${amount} paid by suppliers`,
            commissionUnknown:
                'A product has no rate on the order date, so the amount is not known.',
        },
        tabs: {
            label: 'Order',
            items: 'Items',
            payments: 'Instalments & invoicing',
        },
        items: {
            title: 'List of items',
            badge: (n: number) => `${n.toLocaleString('en-GB')} ${n === 1 ? 'ITEM' : 'ITEMS'}`,
            columns: {
                expand: 'Expand',
                product: 'Product',
                supplier: 'Supplier',
                variant: 'Variant',
                qty: 'Qty',
                unitPrice: 'Unit price',
                lineTotal: 'Line total',
                status: 'Status',
                parcel: 'Parcel',
                estDelivery: 'Est. delivery',
                action: 'Action',
            },
            expand: (name: string, open: boolean) => `${open ? 'Collapse' : 'Expand'} ${name}`,
            edit: (name: string) => `Edit ${name}`,
            editTitle: 'Edit line',
            empty: {
                title: 'No items on this order',
                body: 'Lines added to the order appear here.',
            },
            facts: {
                costPrice: 'Cost price',
                discount: 'Discount',
                notAvailable: 'Not available',
                commission: 'Commission',
                auto: (rate: string) => `${rate} (auto)`,
                aListFee: 'A-List fee',
                taxIncluded: 'Tax included',
                taxRate: 'Tax rate',
                taxAmount: 'Tax amount',
                totalBeforeTax: 'Total before tax',
                origin: 'Origin',
                preOrder: 'Pre-order',
                dropShip: 'Drop ship',
                tracking: 'Tracking',
                inWardrobe: 'In wardrobe',
                returnable: 'Returnable',
                margin: 'Margin',
                neededBy: 'Needed by',
                reason: 'Reason',
            },
        },
        /** The Instalments & invoicing tab (ALS-28, 2694:117525 and 3107:62411). */
        payments: {
            title: 'Instalment schedule',
            badge: (n: number) => `${n} ${n === 1 ? 'INSTALMENT' : 'INSTALMENTS'}`,
            columns: {
                instalment: 'Instalment',
                dueDate: 'Due date',
                amount: 'Amount',
                paymentType: 'Payment type',
                cardFee: 'Card fee',
                status: 'Status',
                invoice: 'Invoice',
            },
            number: (n: number) => `# ${n}`,
            kind: {
                Single: null,
                Deposit: 'Deposit',
                Balance: 'Balance',
                Instalment: null,
            } as const,
            paymentType: { Card: 'Card', BankTransfer: 'Bank transfer' } as const,
            status: { Paid: 'Paid', Due: 'Due', PartlyPaid: 'Part paid' } as const,
            paidSoFar: (amount: string) => `${amount} paid`,
            none: '\u2013',
            /** "1 instalment · £148.00 paid, £54.00 outstanding", the breakdown band's left side. */
            sentence: (count: number, paid: string, outstanding: string) =>
                `${count} ${count === 1 ? 'instalment' : 'instalments'} · ${paid} paid, ${outstanding} outstanding`,
            overpaid: (amount: string) => `${amount} more than the order total`,
            short: (amount: string) => `marked paid ${amount} short of the order total`,
            minis: {
                totalBeforeTax: 'Total before tax',
                totalTax: 'Total tax',
                totalDuty: 'Total duty',
                commission: 'Commission',
                aListFee: 'A-List fee',
                shipping: 'Shipping',
                cardFees: 'Card fees',
                orderTotal: 'Order total',
            },
            /** A cancelled order's schedule stays on the record, with this above it. */
            frozen: {
                single: 'The order was cancelled. Its invoice and payment stay on the record; any sent payment link is void.',
                plan: 'The plan was cancelled with the order. Paid instalments stay on the record; any sent payment links are void.',
            },
            /** Ours, as the cancelled note is the prototype's: why a refunded order's rows still read paid. */
            refunded: {
                single: 'Every item on the order was refunded. Its invoice and payment stay on the record.',
                plan: 'Every item on the order was refunded. Paid instalments stay on the record.',
            },
            record: 'Record payment',
            /** Why Record payment is refused, for its tooltip. */
            recordRefused: {
                NotPermitted: 'Your role cannot record payments. Ask an admin.',
                Deleted: 'A deleted order takes no payments.',
                Cancelled: 'A cancelled order takes no payments. Money owed back is a credit note.',
                NothingOutstanding: 'Nothing is owed on this order.',
            },
            /** The schedule's own way to Cancel order (od-plan-cancel): a plan goes only with its order. */
            cancelPlan: 'Cancel plan & order',
            actions: 'Actions',
            /** The pencil on a plan's unpaid row, which opens Edit payment details (ALS-361). */
            edit: (n: number) => `Edit instalment ${n}`,
            /** Why the pencil is refused, for its tooltip. */
            editRefused: {
                NotPermitted: 'Your role cannot change payment details. Ask an admin.',
                Deleted: 'A deleted order’s plan stays as it was.',
                Cancelled: 'A cancelled order’s plan stays as it was.',
                NothingOutstanding: 'Every instalment is paid.',
            },
            /** Every payment on the order and who recorded it: ours, asked for on ALS-28 (12 Oct 2026). */
            recorded: {
                title: 'Payments recorded',
                badge: (n: number) => `${n} ${n === 1 ? 'PAYMENT' : 'PAYMENTS'}`,
                columns: {
                    on: 'Date',
                    amount: 'Amount',
                    method: 'Received by',
                    by: 'Recorded by',
                    /** Where a payment sent to Xero from here stands there (ALS-456). */
                    xero: 'Xero',
                },
                methods: {
                    BankTransfer: 'Bank transfer',
                    Card: 'Card',
                    Wallet: 'Wallet credit',
                    App: 'Card, in the app',
                    CreditNote: 'Credit note',
                    Xero: 'Paid in Xero',
                } as const,
                /**
                 * The credit notes a payment came off (ALS-29): one, or several as the API joins
                 * them, oldest first, named in the plural as the passbook names them (ALS-481).
                 */
                creditNote: (numbers: string, withWalletCredit = false) => {
                    const named = `${numbers.includes(',') ? 'Credit notes' : 'Credit note'} ${numbers}`;
                    return withWalletCredit ? `${named} and wallet credit` : named;
                },
                inTheApp: 'The client',
                /** Who recorded a payment read back from Xero (ALS-70): nobody here did. */
                byXero: 'Xero',
                /** Part of a Xero payment, the rest being money already recorded by hand. */
                partOfXero: (amount: string) =>
                    `Part of ${amount} paid in Xero; the rest was already recorded`,
                unknown: '–',
                toWallet: (amount: string) => `${amount} more went to wallet credit`,
            },
        },
        /**
         * Record payment: ours, since the prototype takes payments from Xero only. The money goes
         * through the client's wallet, as the old portal records it, and to Xero too unless the
         * person unticks the box (ALS-456), whose words are the ticket's.
         */
        recordPayment: {
            title: 'Record payment',
            subtitle: (orderNo: string, client: string) => `${orderNo} · ${client}`,
            outstanding: (amount: string) => `${amount} outstanding`,
            method: 'Received by',
            methods: {
                BankTransfer: 'Bank transfer',
                Card: 'Card',
                Wallet: 'Wallet credit',
            } as const,
            amount: 'Amount received',
            fillBalance: (amount: string) => `Fill in ${amount}`,
            walletHolds: (amount: string) => `${amount} in the client’s wallet`,
            full: 'Pays the order in full.',
            fullDeposit: 'Pays the deposit. The balance stays due.',
            fullInstalment: (n: number) => `Pays instalment ${n}. The rest stays due.`,
            part: (left: string) => `${left} still outstanding after this.`,
            over: (extra: string, client: string) =>
                `${extra} more than is owed. It goes to ${client}’s wallet as credit.`,
            /** The part above what is owed would go on the wallet, and the Add fund switch is off (ALS-521). */
            overSwitchedOff: (extra: string) =>
                `${extra} more than is owed would go on the client’s wallet, and adjusting wallets is switched off on your account. Record no more than is owed, or ask an admin to turn it on in Users.`,
            fromWallet: (left: string) => `Taken from the wallet, which keeps ${left}.`,
            required: 'Enter the amount received.',
            positive: 'An amount above nothing.',
            pennies: 'To the penny: no more than two decimals.',
            moreThanWallet: (available: string) =>
                `More than the wallet holds. ${available} is available.`,
            moreThanOwed: 'More than is owed. Take no more than the balance from the wallet.',
            /** Wallet credit comes off the client's oldest credit notes first, and Xero is told (ALS-69). */
            footNoteWallet: 'Taken from the oldest credit notes first, and applied in Xero',
            /** Xero applies a pound credit note only to a pound invoice, so the rest is the accountant's (ALS-384). */
            footNoteWalletByHand: (currency: string) =>
                `Taken from the oldest credit notes first, and left to allocate in Xero by hand: the invoice is in ${currency}`,
            /** Recording the payment in Xero too (ALS-456): what ticking does, and what not ticking leaves. */
            xero: {
                label: 'Also record this payment in Xero',
                applies: (invoiceNo: string) =>
                    `Applies it to ${invoiceNo} in Xero too, so its amount due and payment link stay right.`,
                withInvoice:
                    'Goes to Xero with the invoice this payment raises, so its amount due and payment link stay right.',
                owedOnly: (owed: string, extra: string) =>
                    `Only the ${owed} the order owes goes to Xero; the ${extra} over stays wallet credit, for the accountant to record there.`,
                reconnect:
                    'Xero has to be connected again before payments can go there. An admin reconnects it from the Xero badge.',
                warning: (amount: string) =>
                    `Xero will still show this ${amount} as due, so its payment link will charge more than the client owes.`,
                warningAny:
                    'Xero will still show this payment as due, so its payment link will charge more than the client owes.',
                /** A card payment made through Xero's own link is read back from Xero (ALS-70), so it is never recorded twice. */
                cardLink:
                    'Paid through the link on Xero’s invoice? That payment arrives here by itself, so don’t record it. Record only card payments taken another way.',
                footNote: (invoiceNo: string) => `Also recorded in Xero, against ${invoiceNo}`,
                footNoteWithInvoice: 'Also recorded in Xero, with the invoice it raises',
                footNoteHere: 'Recorded here only, not in Xero',
                footNoteOff: 'Recorded here only. Xero isn’t linked.',
                sending: 'It goes to Xero now.',
            },
            cancel: 'Cancel',
            save: 'Record payment',
            done: (amount: string, orderNo: string) => `${amount} recorded against ${orderNo}.`,
            doneOver: (amount: string, orderNo: string, extra: string) =>
                `${amount} recorded against ${orderNo}. ${extra} went to the wallet as credit.`,
            failed: 'The payment could not be recorded.',
        },
        /**
         * Edit payment details (ALS-361), verbatim from screens/order-detail.html's od-pay-panel and
         * order-detail.js's renderPayForm(), in its voice where it has no words.
         */
        editPlan: {
            title: 'Edit payment details',
            subtitle: (orderNo: string, count: number, net: string) =>
                `${orderNo} · ${count} ${count === 1 ? 'instalment' : 'instalments'} · ${net}`,
            carried: (paid: string) => `${paid} already paid is carried over`,
            carriedBody: (outstanding: string, paidRows: number) =>
                `Changing the count or the split redivides the ${outstanding} still outstanding. The paid instalment${paidRows === 1 ? ' keeps' : 's keep'} its amount, date and type.`,
            plan: 'Plan',
            count: 'Number of instalments',
            fewer: 'Fewer instalments',
            more: 'More instalments',
            split: 'Split',
            cardFee: 'Card fee',
            cardFeeValue: (percentage: string) => `${percentage}% · order level`,
            /** A fee typed on New order (ALS-383), which has no rate and follows the card share. */
            cardFeeTyped: 'Typed · order level',
            schedule: 'Schedule',
            scheduleNote: 'paid rows stay as they are',
            columns: {
                number: '#',
                amount: 'Amount',
                dueDate: 'Due date',
                paymentType: 'Payment type',
                status: 'Status',
            },
            row: (n: number) => `instalment ${n}`,
            sentencePaid: (paidRows: number) =>
                `Instalment ${paidRows === 1 ? '1 is' : `1 to ${paidRows} are`} paid and shown for the record.`,
            sentenceEqual: (left: string, open: number) =>
                `The remaining ${left} is split evenly across ${open === 1 ? 'one instalment' : `${open} instalments`}.`,
            sentenceCustom: (left: string, open: number) =>
                `The remaining ${left} is set by hand across ${open === 1 ? 'one instalment' : `${open} instalments`}.`,
            totals: {
                paid: 'Paid',
                stillToPay: 'Still to pay',
                scheduled: 'Scheduled total',
                difference: 'Difference',
            },
            /** What the order's total becomes when a payment type moves the card fee: ours. */
            newTotal: (net: string) =>
                `The order total becomes ${net}, as the card fee follows the payment types.`,
            editable: (open: number, count: number) => `${open} of ${count} instalments editable`,
            footNote: 'Saving does not resend payment links',
            previewFailed: 'The new schedule could not be worked out. Try again in a moment.',
            cancel: 'Cancel',
            save: 'Save changes',
            done: (count: number) => `Payment details saved · ${count} instalments`,
            failed: 'The payment details could not be saved.',
        },
        yes: 'Yes',
        no: 'No',
        /** Edit order, the Sheet / Edit order frame (2818-43568) under the confirmed rules frame. */
        editOrder: {
            title: 'Edit order',
            customerHeading: 'Customer',
            customerNote: 'fixed from creation, because the invoice already exists',
            customerNoteNotInvoiced: 'fixed from creation',
            customer: 'Customer',
            customerLocked:
                'The client carries the billing address and the invoice is already raised. A different client means cancelling this order and raising a new one.',
            customerLockedNotInvoiced:
                'The client carries the billing address the invoice will be raised to. A different client means cancelling this order and raising a new one.',
            relationshipManager: 'Relationship manager',
            contact: 'Contact no',
            email: 'Email',
            country: 'Country',
            wallet: 'Wallet credit',
            walletApplied: 'Wallet credit applied',
            notApplied: 'Not applied',
            notRecorded: 'Not recorded',
            walletAppliedNote: 'Set at creation. To give credit now, raise a credit note.',
            walletAppliedNotRecorded:
                'Raised in the old portal, which kept no record of it. To give credit now, raise a credit note.',
            orderHeading: 'Order',
            orderNo: 'Order no',
            orderDate: 'Order date',
            orderDateOpen:
                'Editable until any money is taken: the order’s commission is worked out from this date.',
            orderDateLocked:
                'Locked once money is taken: the order’s commission is worked out from this date.',
            status: 'Status',
            cancelledNote: 'A cancelled order stays cancelled.',
            saleSource: 'Sales source',
            assignedTo: 'Assigned to',
            assignedToMixed: 'Several people',
            assignedToNone: 'Not assigned',
            assignedToNote: 'Every item on the order is assigned to this person.',
            occasion: 'Occasion',
            occasionNone: 'Not set',
            followUp: 'Follow-up date',
            invoiceType: 'Invoice type',
            quantity: 'Order quantity',
            items: (n: number) => `${n.toLocaleString('en-GB')} ${n === 1 ? 'item' : 'items'}`,
            xeroReference: 'Xero reference',
            xeroReferenceNote: 'Typed by hand until the Xero connection fills it.',
            taxesHeading: 'Taxes & payment',
            currency: 'Currency',
            exchange: 'Exchange',
            cardFee: 'Card fee',
            shippingFee: 'Shipping fee',
            duties: 'Duties',
            aListFee: 'A-List fee',
            /** Under the card fee: fixed from creation, as the frame draws it (ALS-259). */
            cardFeeRate: (rate: string) =>
                `${rate}% of the order, fixed from creation. It follows the total when the lines or fees change.`,
            cardFeeTyped: 'Typed when the order was raised, and fixed from creation.',
            feesOpen: 'Editable until any money is taken. The order’s totals recalculate on save.',
            feeLocked: 'Locked once any money is taken: the client has paid against this figure.',
            feeCancelled: 'A cancelled order keeps the figures it ended with.',
            feesLockedNote:
                'Locked once any money is taken: the client has paid against these figures.',
            feesCancelledNote: 'A cancelled order keeps the figures it ended with.',
            commissionHeading: 'Commission',
            commissionNote: 'from suppliers, on the products that carry it',
            commissionStatus: 'Commission status',
            commissionHint: (amount: string) =>
                `${amount} due from suppliers. Mark it Paid once they have paid it.`,
            commissionHintUnknown: 'Mark it Paid once the suppliers have paid it.',
            addressesHeading: 'Addresses',
            addressesNote: 'from the client’s saved addresses',
            shippingAddress: 'Address (shipping)',
            billingAddress: 'Address (billing)',
            removedAddress: (text: string) => `${text} (removed)`,
            notesHeading: 'Notes',
            externalNote: 'External note for the client',
            externalNotePlaceholder: 'Shown on the invoice. e.g. delivery preferences.',
            remarks: 'Internal remarks',
            remarksPlaceholder: 'Notes for the operations team, not visible to the client.',
            footer: 'No amount changes on save',
            footerRecalculates: 'Order totals recalculate on save',
            footerNote: 'Line items are edited on the Items tab',
            loading: 'Loading the order’s lists',
            cancel: 'Cancel',
            save: 'Save changes',
            saving: 'Saving…',
            saved: (orderNo: string) => `Order ${orderNo} saved`,
            saveFailed: 'The order could not be saved.',
        },
        /**
         * Bulk edit (ALS-261). Ours: no frame draws it. The card head turns into the design
         * system's selection bar, and the drawer is Edit line's status and delivery for several.
         */
        bulk: {
            select: (name: string) => `Select ${name}`,
            selectAll: 'Select every line',
            selected: (n: number) =>
                `${n.toLocaleString('en-GB')} ${n === 1 ? 'line' : 'lines'} selected`,
            of: (n: number) => `of ${n.toLocaleString('en-GB')}`,
            clear: 'Clear selection',
            edit: (n: number) => `Edit ${n.toLocaleString('en-GB')} ${n === 1 ? 'line' : 'lines'}`,
            title: 'Edit lines',
            subtitle: (n: number) =>
                `${n.toLocaleString('en-GB')} ${n === 1 ? 'line' : 'lines'} on this order`,
            statusHeading: 'Status',
            deliveryHeading: 'Delivery',
            deliveryNote:
                'set on every line still moving; a line being returned or cancelled keeps its own',
            leaveAsIs: 'Leave as it is',
            leaveEmpty: 'Left as it is on each line',
            note: 'Only what you fill in is set. Each line takes only what its own status allows, and a status change does everything a single edit does, including notifying the client.',
            foot: 'Fields left empty are left alone',
            save: 'Apply to lines',
            cancel: 'Cancel',
            nothing: 'Choose a status, or fill in a delivery field.',
            saved: (n: number) =>
                `${n.toLocaleString('en-GB')} ${n === 1 ? 'line' : 'lines'} saved`,
            savedNone: 'No line changed',
            skippedTitle: (n: number) =>
                `${n.toLocaleString('en-GB')} ${n === 1 ? 'line was' : 'lines were'} left partly or wholly alone:`,
            done: 'Done',
            failed: 'The lines could not be saved.',
        },
        /** Edit line, the locked frames (2694:116333, 2712:19886): status and delivery over read-only detail. */
        editLine: {
            title: 'Edit line',
            statusHeading: 'Status & delivery',
            statusOnlyNote: 'status only once a return or a cancellation is under way',
            status: 'Product status',
            inWardrobe: 'In wardrobe',
            inWardrobeInactive: 'Active once the status is Delivered.',
            tracking: 'Tracking number',
            trackingPlaceholder: 'Add once the label exists',
            estimatedDelivery: 'Estimated delivery',
            actualDelivery: 'Actual delivery',
            datePlaceholder: 'dd-mmm-yyyy',
            deliveryComment: 'Delivery comment',
            deliveryCommentPlaceholder: 'Anything the team should know about this delivery…',
            followUp: 'Follow-up date',
            followUpPlaceholder: 'Set order follow-up alert date',
            reason: 'Reason',
            reasonPlaceholder: 'Why the item is being cancelled, returned or exchanged',
            accountHeading: 'Xero',
            accountLockedNote: 'locked once the order is invoiced',
            account: 'Xero account',
            accountPlaceholder: 'Select account…',
            accountNote:
                'Where this line posts in Xero. Changing it here leaves the product as it is.',
            accountLocked: 'The invoice keeps the account each line was issued with.',
            detailsHeading: 'Line details',
            detailsNote: 'locked once the order is confirmed',
            detailsSet:
                'To change price, supplier or variant, the order has to go back to Pending confirmation.',
            detailsSetRemovable:
                'Price, supplier and variant are set. The line can still be removed while payment is outstanding.',
            /** The Pending shape (2707:19886): everything open, as the prototype says it (ALS-259). */
            openHeading: 'Product status',
            openStatusNote: 'Everything stays editable until the order is confirmed.',
            /** Under Product status when some statuses need a switch the person does not have (ALS-522). */
            switchedOff: {
                cancel: 'Cancelling orders is switched off on your account, so a line cannot be cancelled. An admin can turn it on in Users.',
                returns:
                    'Returns are switched off on your account, so a line cannot be returned or refunded. An admin can turn them on in Users.',
                both: 'Cancelling orders and returns are switched off on your account, so a line cannot be cancelled, returned or refunded. An admin can turn them on in Users.',
            },
            openLoading: 'Loading the line’s product',
            openFailed:
                'The line’s product could not be loaded, so it cannot be repriced. Try again.',
            facts: {
                qty: 'Qty',
                variant: 'Variant',
                salePrice: 'Sale price',
                costPrice: 'Cost price',
                supplier: 'Supplier',
                commission: 'Commission',
                discount: 'Discount',
                aListFee: 'A-List fee',
                tax: 'Tax',
                origin: 'Country of origin',
                neededBy: 'Needed by',
                dropShip: 'Drop ship',
                preOrder: 'Pre-order',
                inWardrobe: 'In wardrobe',
            },
            total: (amount: string) => `Line total ${amount}`,
            footStatusOnly: 'Status only from here, while a return or a cancellation is under way',
            footLocked: 'Confirmed lines cannot be removed or repriced',
            footOpen: 'Not confirmed yet, so the line can still be changed or removed',
            footOpenOnly: 'Not confirmed yet, so the line can still be changed',
            footRemovable: 'Unpaid, so the line can still be removed from the order',
            footOnly: 'The order’s only line, so it cannot be removed',
            footPaid:
                'Money has been taken on this order, so its lines cannot be removed or repriced',
            remove: 'Remove line',
            removeOnly: 'An order needs at least one line. Cancel the order instead.',
            removeTitle: (name: string) => `Remove ${name}?`,
            removeBody:
                'Nothing has been paid on this order, so the line can be removed. This cannot be undone.',
            removed: (name: string) => `Line removed · ${name} · totals recalculated`,
            removeFailed: 'The line could not be removed.',
            cancel: 'Cancel',
            save: 'Save changes',
            saving: 'Saving…',
            saved: (name: string) => `Line saved · ${name}`,
            saveFailed: 'The line could not be saved.',
        },
        /** Cancel order. The three outcomes are ADR 0049's. */
        cancelDialog: {
            title: (orderNo: string) => `Cancel order ${orderNo}?`,
            Delete: 'Nobody has confirmed it yet, so the client has never seen it. It is deleted, as the old portal deletes an order, and kept for the record.',
            Cancel: 'Nothing has been paid. The order is marked cancelled, the items that have not shipped are cancelled with it, and the client can no longer pay for it.',
            /** The prototype's own words for a paid order, and where the refund is raised (ALS-259). */
            CancelPaid:
                'Money has been taken on this order. Cancelling marks it cancelled, with the items that have not shipped; the refund is raised as a credit note, which opens next.',
            reason: 'Reason',
            reasonPlaceholder: 'Why the order is being cancelled',
            reasonRequired: 'Say why the order is being cancelled.',
            confirm: 'Cancel order',
            keep: 'Keep order',
            doneCancelled: (orderNo: string) => `Order ${orderNo} cancelled`,
            doneDeleted: (orderNo: string) => `Order ${orderNo} deleted`,
            failed: 'The order could not be cancelled.',
        },
    },
    /**
     * New order (ALS-62), verbatim from screens/order-new.html and scripts/screens/order-new.js
     * where the prototype has the words, and in its voice where it does not: it never loads, fails,
     * or meets a client with no address.
     */
    create: {
        breadcrumb: 'Orders',
        title: 'New order',
        /** Re-raise (ALS-260): New order filled from a cancelled order. Ours: no frame draws it. */
        reraise: {
            title: (orderNo: string) => `Raising ${orderNo} again`,
            body: 'Its client is kept, and its lines, details and fees are filled in. The new order gets its own number, and the two link to each other. Change what needs putting right, then create it.',
            dropped: (n: number) =>
                `${n.toLocaleString('en-GB')} ${n === 1 ? 'line was' : 'lines were'} left out: the product is no longer in the catalogue.`,
            failed: 'The cancelled order could not be read, so the form opened empty.',
        },
        subtitle: 'Create an order, add products, then place it to generate the invoice.',
        actorState: 'Creating order',
        create: 'Create order',
        /** The button while the order is being created, as the prototype labels it. */
        creating: 'Creating order…',
        /** Ours: the prototype hands over to the order's page without a word. */
        created: (orderNo: string) => `Order ${orderNo} created`,
        createFailed: 'The order was not created',
        createFailedBody: 'Nothing was saved. Check your connection and try again.',
        cancel: 'Cancel',
        discard: {
            title: 'Discard this order?',
            body: 'Nothing has been created. The form is cleared.',
            confirm: 'Discard',
            keep: 'Keep editing',
        },
        fields: (n: number) => `${n} fields`,
        failed: {
            title: 'New order could not load',
            body: 'The lists it chooses from did not arrive. Try again in a moment.',
            action: 'Try again',
        },
        details: {
            title: 'Order details',
            noClient: 'No customer selected',
            customer: 'Customer',
            customerPlaceholder: 'Search by name, email, or phone…',
            customerSearch: 'Name, email or phone',
            customerNone: 'No customer matches.',
            customerHelp: 'Selecting a customer fills RM, wallet, contact, email and country.',
            customerFilled: 'Filled from the customer record. Edit if this order differs.',
            customerLoading: 'Filling from the customer record…',
            customerFailed: 'The customer record could not be read. Choose the customer again.',
            customerGone: 'That customer can no longer take an order. Choose another.',
            /** Re-raise keeps the cancelled order's client (ALS-519). Ours: no frame draws it. */
            customerKept:
                'Kept from the cancelled order. For another client, create a new order instead.',
            customerKeptGone:
                'That customer can no longer take an order, so this order cannot be raised again. Create a new order instead.',
            customerKeptFailed:
                'The customer record could not be read. Reload the page to try again.',
            addClient: 'Add a new client',
            pending: 'Pending',
            pendingCustomer: 'Pending customer',
            wallet: 'Wallet credit',
            noWallet: 'No wallet',
            orderNo: 'Order no',
            orderNoPlaceholder: 'On create',
            relationshipManager: 'Relationship Manager',
            contact: 'Contact No',
            email: 'Email',
            country: 'Country',
            orderDate: 'Order Date',
            occasion: 'Occasion',
            occasionNone: 'None',
            occasionAdd: 'Add special date…',
            occasionAdded: (text: string) => `Special date added · ${text}`,
            salesTeam: 'Sales team',
            salesTeamPlaceholder: 'Select team…',
            salesTeamNone: 'Not set',
            saleSource: 'Sales Source',
            saleSourcePlaceholder: 'Select source…',
            currency: 'Currency',
            exchange: 'Exchange',
            status: 'Status',
            quantity: 'Order quantity',
            items: (n: number) => `${n.toLocaleString('en-GB')} ${n === 1 ? 'item' : 'items'}`,
            shippingFee: 'Shipping fee',
            duties: 'Duties',
            /** The fee field's treatment menu, named for the fee it belongs to (ALS-27). */
            feeTax: (fee: string) => `${fee} tax treatment`,
            feeTaxWaiting: 'Loading…',
            cardFee: 'Card fee',
            /**
             * The note under the card fee and its action (ALS-383). Our words: the prototype draws
             * a rate field with no note.
             */
            cardFeeWhole: (percent: string) => `${percent}% of the order`,
            cardFeeShare: (percent: string) => `${percent}% of the card share`,
            cardFeeNoCard: 'No instalment is paid by card',
            cardFeeTyped: 'Typed',
            cardFeeMoved: (charged: string) => `Typed · ${charged} on the card share now`,
            cardFeeRate: (percent: string, calculated: string) => `(${percent}% is ${calculated})`,
            recalculate: 'Recalculate',
            recalculateLabel: 'Recalculate the card fee',
            aListFee: 'A-List fee',
            aListFeeUnit: 'A-List fee unit',
            aListFeeUnits: { Amount: 'Amount', Percentage: 'Percentage' },
            taxTreatment: 'Tax treatment',
            exportLabel: 'Zero rated · export',
            exportWaiting: 'Set from the client’s country once a customer is chosen.',
            exportOn: 'Goods leave the UK: every line is zero rated. The A List fee keeps VAT 20%.',
            exportOff: 'UK delivery: lines carry their product’s tax row. Switch on for an export.',
            /** The exchange rate's second look (ALS-26). Our words; the prototype has none. */
            rateAway: (percent: number, last: string, code: string) =>
                `${percent}% away from ${last}, the rate the last ${code} order used. Check it before creating the order.`,
        },
        /** Deposit & instalment plan (ALS-25), verbatim from screens/order-new.js's renderInstal(). */
        instalments: {
            title: 'Deposit & instalment plan',
            note: 'Off: single payment link for the full amount. On: deposit plus balance schedule with equal or custom split.',
            switchLabel: 'Deposit and instalment plan',
            count: 'Number of instalments',
            fewer: 'Fewer instalments',
            more: 'More instalments',
            split: 'Split',
            splits: { Equal: 'Equal', Custom: 'Custom' },
            columns: {
                number: '#',
                amount: 'Amount',
                dueDate: 'Due date',
                paymentType: 'Payment type',
                cardFee: 'Card fee amount',
            },
            paymentTypes: { Card: 'Card', BankTransfer: 'Bank transfer' },
            onInvoice: 'Due on invoice',
            /** The row's name for the controls in it, which share one header. */
            row: (n: number) => `instalment ${n}`,
            ordinal: (n: number) => `${ORDINALS[n - 1] ?? numberedOrdinal(n)} instalment`,
            scheduled: 'Total scheduled',
            cardFeeTotal: 'Card fee total',
            short: (amount: string) => `${amount} short`,
            over: (amount: string) => `${amount} over`,
            errorBody: (count: number, total: string, due: string, gap: string, short: boolean) =>
                `The ${COUNT_WORDS[count] ?? count} instalments total ${total} against a net amount of ${due}, so ${gap} ${short ? 'is unallocated' : 'is over'}. Adjust an amount, or switch back to Equal to split it evenly.`,
            footnote:
                'Card fee is allocated proportionally across instalments and is never refunded. Bank-transfer instalments carry no card fee.',
            /** A bank-transfer row's card fee, which it has none of. */
            none: '–',
        },
        /** Totals & margins (ALS-26), verbatim from screens/order-new.html's renderTotals(). */
        totals: {
            title: 'Totals & margins',
            totalBeforeTax: 'Total before tax',
            duties: 'Total duty',
            tax: 'Total tax',
            shipping: 'Shipping fees',
            aListFee: 'A List fee',
            aListFeeShare: (percent: string) => `A List fee · ${percent}%`,
            cardFee: 'Card fee',
            applyWallet: 'Apply wallet credit',
            walletApplied: (applied: string, left: string | null) =>
                left
                    ? `${applied} applied · ${left} left on account`
                    : `${applied} applied · nothing left on account`,
            walletAvailable: (balance: string) => `${balance} available on account`,
            walletNone: 'No credit on this account',
            walletPending: 'Pending customer',
            net: 'Net amount',
            margin: 'Estimated margin',
            marginValue: (amount: string, percent: number) => `${amount} · ${percent}%`,
            covered: 'Wallet credit covers this order',
            inBase: (pounds: string) => `${pounds} in base currency`,
            failed: 'The totals could not be worked out. They are tried again as the order changes.',
            working: 'Working out the totals…',
        },
        shipping: {
            title: 'Billing & shipping',
            waiting: 'Will pre-fill from customer',
            filled: 'Pre-filled from customer',
            billingAddress: 'Address (Billing)',
            shippingAddress: 'Address (Shipping)',
            pendingAddress: 'Pending customer…',
            noAddress: 'No saved address',
            noAddressOn: (first: string) =>
                `No saved address on ${first}’s record yet. Add one with Add address.`,
            addAddress: 'Add address',
            /** The card head's line once an address is in, named the way the record's list names it. */
            addressSaved: (first: string, where: string) =>
                `Address saved to ${first}’s record · ${where}`,
            /** New order's Add address dialog (ALS-406). */
            dialog: {
                title: 'Add address',
                // The prototype's "so her next order has it", without guessing a pronoun.
                savedTo: (name: string) =>
                    `Saved to ${name}’s client record too, so their next order has it.`,
                forThisOrder: 'Use on this order for',
                billing: 'Billing',
                shipping: 'Shipping',
                cancel: 'Cancel',
                save: 'Save address',
            },
            followUp: 'Follow-up date',
            xeroReference: 'Xero reference',
            xeroReferencePlaceholder: 'External Xero ref',
            externalNote: 'External note for the client (Will show on the invoice)',
            externalNotePlaceholder:
                'Note shown to the client on the invoice. e.g. delivery preferences.',
            remarks: 'Internal remarks (Business only)',
            remarksPlaceholder: 'Notes for the operations team, not visible to the client.',
        },
        errors: {
            customer:
                'Pick the customer first. The order, the invoice and the wallet all belong to them.',
            orderDate: 'Choose the order date.',
            orderDateFuture: 'Not a date in the future.',
            followUpPast: "Follow-up date can't be in the past.",
            saleSource: 'Choose a sale source.',
            currency: 'Choose the currency.',
            exchange: 'The exchange rate must be above zero.',
            exchangeFormat: 'A number with at most six decimals.',
            shippingAddress: 'Choose a shipping address.',
            billingAddress: 'Choose a billing address.',
            xeroReference: 'At most 100 characters.',
            remarks: 'At most 500 characters.',
            externalNote: 'At most 4,000 characters.',
            lines: 'Add at least one product.',
            fee: 'An amount to the penny, no less than zero.',
            percentage: 'A percentage from 0 to 100.',
            planAddsUp: 'Instalments do not add up to the net amount',
            planRows: 'Put right the instalments marked in the plan.',
            planDueDates: 'Give every instalment after the first its due date.',
            dueBeforeOrder: 'Not before the order date.',
            dueOutOfOrder: 'Not before the instalment above.',
            dueMissing: 'Choose its due date.',
            instalmentAmount: 'An amount to the penny, above nothing.',
        },
    },
    /**
     * The line editor and New order's List of products (ALS-63), verbatim from
     * scripts/components/line-editor.js and scripts/screens/order-new.js where the prototype has
     * the words.
     */
    lines: {
        cardTitle: 'List of products',
        count: (n: number) => `${n} ${n === 1 ? 'product' : 'products'}`,
        empty: {
            title: 'No products added yet',
            body: 'Use Add product to pull from a lookbook or the catalogue, or Create product to paste a retailer URL.',
        },
        columns: {
            expand: 'Expand',
            product: 'Product',
            supplier: 'Supplier',
            unitPrice: 'Unit price',
            neededBy: 'Needed by',
            status: 'Status',
            actions: 'Actions',
        },
        expand: (name: string, open: boolean) => `${open ? 'Collapse' : 'Expand'} ${name}`,
        edit: (name: string) => `Edit ${name}`,
        duplicate: (name: string) => `Duplicate ${name}`,
        remove: (name: string) => `Remove ${name}`,
        editTitle: 'Edit line',
        duplicateTitle: 'Duplicate',
        removeTitle: 'Remove',
        off: (percent: number) => `${percent}% off`,
        repeated: 'On the order more than once',
        times: (qty: number) => `× ${qty}`,
        facts: {
            quantity: 'Quantity',
            discount: 'Discount',
            costPrice: 'Cost price',
            tax: 'Tax',
            lineTotal: 'Line total',
            none: 'None',
        },
        removeDialog: {
            title: 'Remove this product from the order?',
            body: (name: string) =>
                `${name} will be taken off the order. Totals recalculate. This cannot be undone.`,
            keep: 'Keep it',
            confirm: 'Remove product',
        },
        editor: {
            fulfilment: 'Variant & fulfilment',
            pricing: 'Pricing & margin',
            qty: 'Qty',
            size: 'Size',
            sizePlaceholder: 'Select size…',
            noSizes: 'One size',
            colour: 'Colour',
            colourPlaceholder: 'Select colour…',
            noColours: 'No colour',
            taxIncluded: 'Tax incl.',
            tax: 'Tax',
            taxPlaceholder: 'Select tax…',
            account: 'Xero account',
            accountPlaceholder: 'Select account…',
            origin: 'Country of origin',
            originPlaceholder: 'Select country…',
            dropShip: 'Drop ship',
            dropShipNote: 'The supplier delivers to the client directly.',
            preOrder: 'Pre-order',
            neededBy: 'Needed by',
            availableFrom: 'Available from',
            availableFromNote: 'When the piece is released. Pre-order follow-ups count from it.',
            salePrice: 'Sale price',
            costPrice: 'Cost price',
            costFromDiscount: 'Worked out from the discount.',
            supplier: 'Supplier',
            supplierPlaceholder: 'Select supplier…',
            supplierSearch: 'Search suppliers…',
            supplierNone: 'No supplier matches.',
            supplierUnavailable: (name: string) =>
                `${name}, the product’s supplier, is blacklisted and takes no new orders. Choose another.`,
            supplierPending: 'Pending approval',
            commission: 'Commission',
            commissionRate: (rate: number) => `${rate}% from the supplier`,
            commissionNone: 'None for this supplier',
            discount: 'Discount',
            discountOff: 'Not available',
            aListFee: 'A-List fee',
            note: 'Auto commission is pre-filled from the supplier. Discount is off while commission applies; the A-List fee is always available and defaults to 0.',
            yes: 'Yes',
            no: 'No',
            totalBeforeTax: 'Total before tax',
            taxAmount: 'Tax',
            lineTotal: 'Line total',
        },
        /** The Add product menu and its two drawers (ALS-64), from screens/order-new.html. */
        add: {
            menu: 'Add product',
            menuLookbook: 'From Lookbook',
            menuDatabase: 'From Database',
            menuCreate: 'Create new product',
            lookbookTitle: 'Add from lookbook',
            lookbookSubtitle: 'Select a lookbook, then tick products and set each line.',
            databaseTitle: 'Add from database',
            databaseSubtitle: 'Search the catalogue, then set quantity, variant and pricing.',
            lookbook: 'Lookbook',
            lookbookPlaceholder: 'Select a lookbook…',
            lookbookSearch: 'Search lookbooks…',
            lookbookNoMatch: 'No lookbook matches.',
            lookbookNoClient: {
                title: 'Choose the customer first',
                body: 'Lookbooks are the client’s own and the ones made for everyone. Pick the order’s customer above to see theirs.',
            },
            lookbookNone: {
                title: 'No lookbooks for this client',
                body: 'Nothing made for them or for everyone is live.',
            },
            lookbookEmpty: {
                title: 'Choose a lookbook to start',
                body: 'Products appear grouped by page, in the order the stylist arranged them.',
            },
            notLive: (date: string) => `Goes live ${date}`,
            noProducts: 'No catalogue products in it',
            personal: 'Just for this client',
            productCount: (n: number) => `${n} ${n === 1 ? 'product' : 'products'}`,
            meta: (products: number, pages: number) =>
                `${products} ${products === 1 ? 'product' : 'products'} · ${pages} ${pages === 1 ? 'page' : 'pages'}`,
            page: (n: number) => `Page ${n}`,
            selectPage: 'Select page',
            selectAll: 'Select all',
            groupedByPage: 'Grouped by page',
            search: 'Product',
            searchPlaceholder: 'Search by name, code or brand…',
            searchEmpty: {
                title: 'Nothing matches that search',
                body: 'Try the product code, or a shorter piece of the name.',
            },
            catalogueEmpty: {
                title: 'The catalogue is empty',
                body: 'There are no active products to add.',
            },
            showMore: 'Show more',
            matches: (shown: number, total: number) =>
                `${shown.toLocaleString('en-GB')} of ${total.toLocaleString('en-GB')} products`,
            addedOn: (date: string) => `Added ${date}`,
            alreadyOnOrder: 'Already on this order',
            unavailable: {
                Archived: 'Archived in the catalogue, so it cannot go on an order',
                NotInCatalogue: 'Not in the catalogue, so it cannot go on an order',
            },
            loadingLine: 'Reading the product…',
            lineFailed: 'This product could not be read. Untick it and try again.',
            intro: 'Price, tax and supplier are pre-filled from the product. Edit per line.',
            noneSelected: 'No products selected',
            noLookbookSelected: 'No lookbook selected',
            selected: (n: number) => `${n} ${n === 1 ? 'product' : 'products'} selected`,
            fromLookbook: (name: string) => `from ${name}`,
            pickLookbook: 'Pick one to see its products',
            fromCatalogue: 'from the product catalogue',
            cancel: 'Cancel',
            addToOrder: 'Add to order',
            addN: (n: number) => `Add ${n} ${n === 1 ? 'product' : 'products'} to order`,
            fixLines: 'Some lines need a value before they can be added.',
            added: (n: number) => `${n} ${n === 1 ? 'product' : 'products'} added to the order`,
            switchDialog: {
                title: 'Discard the products you picked?',
                body: (n: number, from: string) =>
                    `${n} ${n === 1 ? 'product' : 'products'} from ${from} ${n === 1 ? 'has' : 'have'} not been added to the order yet. Switching lookbook clears it.`,
                keep: 'Keep my selection',
                confirm: 'Discard and switch',
            },
            failed: {
                title: 'The list could not load',
                body: 'Try again in a moment.',
                action: 'Try again',
            },
        },
        /** Create product (2927:56082), the third Add product route. */
        create: {
            title: 'Create product',
            subtitle: 'Paste a retailer link, or enter the details yourself.',
            intro: {
                title: 'Product details',
                body: 'Filled from the link where possible. Everything here can be edited or typed by hand.',
            },
            sections: {
                details: 'Product details',
                pricing: 'Pricing & tax',
                categories: 'Category mapping',
                variant: 'Variant',
                settings: 'Settings',
                notes: 'Notes',
            },
            photo: {
                title: 'Product photo',
                none: 'One image, shown to the client on the order and in the wardrobe.',
                one: 'One image is attached.',
                many: (n: number) => `${n} images from the product page. The first is main.`,
                failed: 'The photo did not upload. Try another file.',
                upload: 'Upload photo',
                replace: 'Replace photo',
            },
            name: 'Product name',
            namePlaceholder: 'e.g. Eleonora silk-blend gown',
            brand: 'Brand',
            supplier: 'Supplier',
            selectOrAdd: 'Select or add…',
            searchBrands: 'Search brands…',
            searchSuppliers: 'Search suppliers…',
            noBrand: 'No brand matches.',
            noSupplier: 'No supplier matches.',
            addBrand: 'Add a new brand',
            addSupplier: 'Add a new supplier',
            brandNote: 'Not listed? Add a new brand.',
            supplierNote: 'Not listed? Add a new supplier.',
            description: 'Description',
            descriptionPlaceholder: 'What the client sees on the invoice…',
            code: 'Product code',
            codePlaceholder: 'On create',
            style: 'Style',
            select: 'Select…',
            invoiceDescription: 'Invoice description',
            invoiceDescriptionPlaceholder: 'Shorter line for the Xero invoice, if it differs…',
            currency: 'Currency',
            currencyNote: 'The order sets it, so every line reconciles.',
            sellingPrice: 'Selling price',
            costPrice: 'Cost price',
            taxIncluded: 'Tax included',
            yes: 'Yes',
            no: 'No',
            tax: 'Tax',
            taxPlaceholder: 'Select rate…',
            xeroAccount: 'Xero account',
            xeroPlaceholder: 'Select account…',
            tariffCode: 'Tariff code',
            tariffPlaceholder: 'e.g. 6204.43',
            tariffSearch: 'Search codes…',
            tariffNone: 'No code matches.',
            tariffNote: 'From the commodity-codes library, not free text.',
            commission: 'Commission',
            aListFee: 'A-List fee',
            aListFeeNote: 'On this order’s line, not the product.',
            otherCurrency: (theirs: string, order: string) =>
                `The page priced it in ${theirs || 'another currency'}. Enter it in ${order}.`,
            aListFeeInvalid: 'A fee of zero or more.',
            primary: 'Primary category',
            secondary: 'Secondary category',
            tertiary: 'Tertiary category',
            optional: 'Optional',
            country: 'Country of origin',
            searchCountries: 'Search countries…',
            noCountry: 'No country matches.',
            colour: 'Colour',
            sizeType: 'Size type',
            sizes: 'Sizes',
            sizeTypeFirst: 'Pick a size type first',
            sizesRequired: 'Pick the size, or clear the size type.',
            switches: {
                active: {
                    label: 'Active',
                    note: 'An inactive product stays on history but cannot be picked again.',
                },
                wardrobe: {
                    label: 'Show in Wardrobe',
                    note: 'The client sees it in their wardrobe once it is delivered.',
                },
                returnable: {
                    label: 'Returnable',
                    note: 'Drives the return window messaging on the order.',
                },
            },
            remarks: 'Remarks',
            remarksPlaceholder: 'Anything the team should know…',
            /** What blocks Create, in the frame's words (2927:56646), and the name each goes by. */
            requiredMessages: {
                name: 'A product name is required.',
                brandId: 'Pick a brand, or add a new one.',
                supplierId: 'Pick a supplier, or add a new one.',
                description: 'A description is required.',
                currencyId: 'The order sets the currency.',
                costPrice: 'A cost price is required.',
                taxId: 'Pick the tax rate that applies.',
                xeroMappingId: 'Pick the account this sale posts to.',
                categoryId: 'Pick a primary category.',
            },
            requiredNames: {
                name: 'name',
                brandId: 'brand',
                supplierId: 'supplier',
                description: 'description',
                currencyId: 'currency',
                price: 'selling price',
                costPrice: 'cost price',
                taxId: 'tax rate',
                xeroMappingId: 'Xero account',
                categoryId: 'primary category',
            },
            missingTitle: (n: number) =>
                n === 1 ? 'One field is still needed' : `${n} fields are still needed`,
            missingBody: (names: string[]) => {
                const list = names.join(', ');
                const one = names.length === 1;
                return `${list.charAt(0).toUpperCase()}${list.slice(1)} ${one ? 'is' : 'are'} required before this product can be created. ${one ? 'It is' : 'They are'} marked below.`;
            },
            /** The footer's three states, as the frame writes them (2927:56170). */
            required: (n: number) => `${n} required ${n === 1 ? 'field' : 'fields'}`,
            stillNeeded: (n: number) => `${n} ${n === 1 ? 'field' : 'fields'} still needed`,
            ready: 'Ready to save',
            footerNote: 'Saved to the catalogue and added to this order',
            notSaved: 'Nothing has been saved yet',
            cancel: 'Cancel',
            save: 'Create & add to order',
            added: (code: string) => `Created ${code} and added it to the order`,
            notAdded: (code: string) =>
                `Created ${code}, but it could not be put on the order. Add it from the database.`,
        },
        sheet: {
            title: 'Edit product',
            footerNote: 'Not confirmed yet, so the line can still be changed or removed',
            cancel: 'Cancel',
            save: 'Save changes',
        },
        errors: {
            quantity: 'At least one, in whole pieces.',
            tax: 'Choose a tax from the register.',
            money: 'An amount, no less than zero.',
            costPrice: 'Enter the cost price.',
            supplier: 'Choose the supplier.',
            account: 'Pick the account this sale posts to.',
            discount: 'A whole percentage, 0 to 100.',
            availableFrom: 'A pre-order needs the date the piece is available.',
            neededByBeforeAvailable: 'Not before the piece is available.',
        },
    },
    /**
     * Comment & attachments (ALS-65), from screens/order-detail.html and order-new.html: the
     * order's internal journal.
     */
    comments: {
        title: 'Comment & attachments',
        subtitle: 'Internal, never shown to the client',
        count: (n: number) => `${n.toLocaleString('en-GB')} ${n === 1 ? 'comment' : 'comments'}`,
        label: 'New comment',
        placeholder: 'Add a note for the team…',
        required: 'Write the comment first.',
        attach: 'Attach a file',
        attachHint: 'PDF, JPG or PNG · max 10 MB each',
        accept: 'application/pdf,image/png,image/jpeg',
        remove: (name: string) => `Remove ${name}`,
        post: 'Post comment',
        posted: 'Comment added',
        failed: 'The comment could not be added.',
        filesFailed: (names: string[]) =>
            `The comment was added, but ${names.length === 1 ? 'this file was' : 'these files were'} not: ${names.join(', ')}.`,
        empty: 'No comments',
        emptyBody: 'Internal notes the team leaves on this order appear here, newest first.',
        unknown: 'Unknown',
        readOnly: 'This order is kept for the record and takes no new comments.',
        loadFailed: 'The comments could not load.',
        retry: 'Try again',
        download: (name: string) => `Download ${name}`,
        /** New order: comments need the order to exist, as a product's do (ALS-65). */
        newOrder:
            'Comments and attachments are added once the order is created. Internal working notes, never shown to the client.',
    },
} as const;
